import React, { useEffect, useMemo, useState } from "react";
import { getSuperAdminReport, SuperAdminReportData } from "../../services/superAdminApi";

const cardCls = "bg-white rounded-xl shadow-sm border border-gray-200 p-4";

const SuperAdminReports: React.FC = () => {
  const today = useMemo(() => new Date().toISOString().slice(0, 10), []);
  const thirtyDaysAgo = useMemo(() => {
    const date = new Date();
    date.setDate(date.getDate() - 30);
    return date.toISOString().slice(0, 10);
  }, []);
  const [startDate, setStartDate] = useState(thirtyDaysAgo);
  const [endDate, setEndDate] = useState(today);
  const [data, setData] = useState<SuperAdminReportData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const load = async () => {
    try {
      setLoading(true);
      setError("");
      const res = await getSuperAdminReport(startDate, endDate);
      setData(res);
    } catch (e: any) {
      setError(e?.response?.data?.message || "Failed to load reports.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  return (
    <div className="space-y-5 p-1">
      <div>
        <h1 className="text-2xl font-semibold text-[#402B69]">Superadmin Reports</h1>
        <p className="text-sm text-gray-500">
          Platform analytics across bookings, payments, and company growth.
        </p>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 flex flex-wrap gap-3 items-end">
        <div>
          <label className="block text-xs text-gray-500 mb-1">Start Date</label>
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="border rounded-lg px-3 py-2 text-sm"
          />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">End Date</label>
          <input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            className="border rounded-lg px-3 py-2 text-sm"
          />
        </div>
        <button
          onClick={load}
          className="bg-[#885ABB] hover:bg-[#7748A4] text-white px-4 py-2 rounded-lg text-sm font-semibold"
        >
          Apply
        </button>
        <p className="text-xs text-gray-500 ml-auto">
          Range: {data?.range?.start_date || startDate} to {data?.range?.end_date || endDate}
        </p>
      </div>

      {loading ? (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 text-gray-600">
          Loading reports...
        </div>
      ) : error ? (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 text-red-600">
          {error}
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
            <div className={cardCls}>
              <p className="text-xs text-gray-500">Total Companies</p>
              <p className="text-2xl font-bold text-[#402B69]">{data?.companies.total ?? 0}</p>
            </div>
            <div className={cardCls}>
              <p className="text-xs text-gray-500">Active Companies</p>
              <p className="text-2xl font-bold text-green-600">{data?.companies.active ?? 0}</p>
            </div>
            <div className={cardCls}>
              <p className="text-xs text-gray-500">Inactive Companies</p>
              <p className="text-2xl font-bold text-red-600">{data?.companies.deactive ?? 0}</p>
            </div>
            <div className={cardCls}>
              <p className="text-xs text-gray-500">Total Bookings</p>
              <p className="text-2xl font-bold text-[#402B69]">{data?.bookings.total ?? 0}</p>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className={cardCls}>
              <h2 className="text-sm font-semibold text-[#402B69] mb-3">Payments Summary</h2>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500">Total Amount</span>
                  <span className="font-semibold">{(data?.payments.total_amount ?? 0).toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Total Commission</span>
                  <span className="font-semibold">{(data?.payments.total_commission ?? 0).toFixed(2)}</span>
                </div>
                <div className="border-t pt-2 mt-2 flex justify-between">
                  <span className="text-gray-700 font-medium">Net Income</span>
                  <span className="font-bold text-[#402B69]">{(data?.payments.net_income ?? 0).toFixed(2)}</span>
                </div>
              </div>
            </div>

            <div className={cardCls}>
              <h2 className="text-sm font-semibold text-[#402B69] mb-3">Bookings Breakdown</h2>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <p className="text-gray-500">Completed</p>
                  <p className="font-semibold text-[#402B69]">{data?.bookings.completed ?? 0}</p>
                </div>
                <div>
                  <p className="text-gray-500">Canceled</p>
                  <p className="font-semibold text-[#402B69]">{data?.bookings.canceled ?? 0}</p>
                </div>
                <div>
                  <p className="text-gray-500">Online</p>
                  <p className="font-semibold text-[#402B69]">{data?.bookings.online ?? 0}</p>
                </div>
                <div>
                  <p className="text-gray-500">POS</p>
                  <p className="font-semibold text-[#402B69]">{data?.bookings.pos ?? 0}</p>
                </div>
              </div>
            </div>
          </div>

          <div className={cardCls}>
            <h2 className="text-sm font-semibold text-[#402B69] mb-3">Company Growth</h2>
            {(data?.companies.growth ?? []).length === 0 ? (
              <p className="text-sm text-gray-500">No growth entries in selected date range.</p>
            ) : (
              <div className="max-h-80 overflow-y-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50 border-b sticky top-0">
                    <tr>
                      <th className="text-left px-3 py-2 font-semibold text-gray-700">Date</th>
                      <th className="text-left px-3 py-2 font-semibold text-gray-700">New Companies</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(data?.companies.growth ?? []).map((row) => (
                      <tr key={row.date} className="border-b last:border-b-0">
                        <td className="px-3 py-2 text-gray-700">{row.date}</td>
                        <td className="px-3 py-2 text-gray-700 font-medium">{row.total}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default SuperAdminReports;

